import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { getCurrentAccount, getCurrentUser } from '../lib/auth';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
} from './ui/dropdown-menu';
import { Bell, AlertTriangle, Info, AlertOctagon, CheckCircle2 } from 'lucide-react';

const SEV = {
  error: { icon: AlertOctagon, cls: 'text-rose-600', dot: 'bg-rose-500' },
  warning: { icon: AlertTriangle, cls: 'text-amber-600', dot: 'bg-amber-500' },
  info: { icon: Info, cls: 'text-blue-600', dot: 'bg-blue-500' },
  success: { icon: CheckCircle2, cls: 'text-emerald-600', dot: 'bg-emerald-500' },
  collector: { icon: CheckCircle2, cls: 'text-emerald-600', dot: 'bg-emerald-500' },
};

// Relative "received time" for persisted notifications (e.g. "2m ago").
const relTime = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

const fullTime = (iso) => {
  if (!iso) return '';
  const dt = new Date(iso);
  return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleString();
};

const DISMISS_KEY = 'bp_dismissed_alerts';
const getDismissed = () => {
  try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]'); } catch { return []; }
};

// A persisted DB notification carries a `created_at`; ephemeral computed
// billing/usage alerts (past_due, low_ai_credits, …) do not.
const isPersisted = (a) => !!a.created_at;
// A user-scoped notification (ESG / KPI / Payment-file escalation) has a
// `user_id`; account-scoped ones (collectors, missing bills) do not — those
// keep the per-browser localStorage dismiss behaviour.
const isUserScoped = (a, uid) => isPersisted(a) && !!a.user_id && a.user_id === uid;

export default function NotificationBell() {
  const navigate = useNavigate();
  const acct = getCurrentAccount();
  const user = getCurrentUser();
  const uid = user?.id;
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!acct?.id) return;
    try {
      // /subscription/alerts is the COMPLETE feed: it merges persisted in-app
      // notifications (collectors, missing-bills, ESG, KPI, payment-file
      // escalations) on top of the live computed billing/usage alerts.
      const { data } = await api.get('/subscription/alerts', { params: { account_id: acct.id } });
      const dismissed = getDismissed();
      setAlerts((data?.alerts || []).map((a) => ({
        ...a,
        // Server-persisted read state for user-scoped notifications; otherwise
        // fall back to the local dismiss list.
        read: isUserScoped(a, uid) ? !!a.read : dismissed.includes(a.id),
      })));
    } catch (e) { /* silent */ }
  }, [acct?.id, uid]);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const unread = alerts.filter((a) => !a.read).length;

  const markAllRead = async () => {
    // Locally dismiss ephemeral + account-scoped items…
    const localIds = alerts.filter((a) => !isUserScoped(a, uid)).map((a) => a.id);
    const ids = Array.from(new Set([...getDismissed(), ...localIds]));
    localStorage.setItem(DISMISS_KEY, JSON.stringify(ids));
    setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
    // …and persist read state server-side for the user's own notifications.
    if (acct?.id && alerts.some((a) => isUserScoped(a, uid) && !a.read)) {
      try { await api.post('/notifications/read-all', null, { params: { account_id: acct.id } }); }
      catch (e) { /* silent */ }
    }
  };

  const openAlert = async (a) => {
    setOpen(false);
    if (isUserScoped(a, uid)) {
      if (acct?.id && !a.read) {
        try { await api.post(`/notifications/${a.id}/read`, null, { params: { account_id: acct.id } }); }
        catch (e) { /* silent */ }
      }
      if (a.action_url) navigate(a.action_url);
      return;
    }
    // Deep-link: bill-cap alerts jump straight into an Upgrade checkout.
    if (a.checkout_plan && acct?.id) {
      try {
        const { data } = await api.post('/subscription/checkout', {
          plan_id: a.checkout_plan, origin_url: window.location.origin, account_id: acct.id,
        });
        if (data?.url) { window.location.href = data.url; return; }
      } catch (e) { /* fall back to plan picker */ }
    }
    if (a.action_url) navigate(a.action_url);
  };

  return (
    <DropdownMenu open={open} onOpenChange={(o) => { setOpen(o); if (o) markAllRead(); }}>
      <DropdownMenuTrigger asChild>
        <button className="relative p-2 hover:bg-muted transition-colors" data-testid="notification-bell">
          <Bell className="h-4 w-4 text-muted-foreground" />
          {unread > 0 && (
            <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 flex items-center justify-center bg-rose-500 text-white text-[10px] font-bold rounded-full"
                  data-testid="notification-badge">
              {unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notification-dropdown">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <span className="text-sm font-black tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>Alerts</span>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{alerts.length} total</span>
        </div>
        {alerts.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="notification-empty">
            <CheckCircle2 className="h-6 w-6 mx-auto mb-2 text-emerald-500" />
            You're all caught up.
          </div>
        ) : (
          <div className="max-h-96 overflow-y-auto">
            {alerts.map((a) => {
              const sev = SEV[a.type] || SEV[a.severity] || SEV.info;
              const Icon = sev.icon;
              return (
                <button
                  key={a.id}
                  onClick={() => openAlert(a)}
                  className="w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-muted transition-colors flex gap-3"
                  data-testid={`notification-item-${a.id}`}
                >
                  <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${sev.cls}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-foreground">{a.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{a.message}</div>
                    {a.created_at && (
                      <div className="text-[10px] text-muted-foreground/70 mt-1" title={fullTime(a.created_at)}
                           data-testid={`notification-time-${a.id}`}>
                        {relTime(a.created_at)}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
